import { uniqBy } from 'lodash';
import {
  BOOKING_STATES,
  BookingState,
  CCG_BOOKING_RESTRICTION,
  NON_CCG_BOOKING_RESTRICTION,
} from '../constants/bookings';
import { AVAILABILITY_STATUSES } from '../constants/fieldData';
import recurrenceRuleBuilder, {
  nextOccurrence,
  RecurrenceOptions,
} from '../services/recurrenceRuleBuilder';
import { dateFormatForAPI } from './date';
import moment from './dayjs';

export const loadingStatuses = {
  OPENING_CLOSING: 'openingClosingTimes',
  SERVICE_TYPES: 'serviceTypes',
  AVAILABILITY: 'availability',
};

const weekdays = [
  'sunday',
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
] as const;

interface Closure {
  startDate: string;
  endDate: string;
}

interface BookingItem {
  id?: number;
  date: string;
  startTime: string;
  endTime: string;
  spaceId: number;
  state: BookingState;
}

interface Slot {
  startTime: string;
  endTime: string;
  combined?: boolean;
}

interface CalendarItems {
  [date: string]: { [space: string]: Slot[] };
}

// A single row inside the availability object built by hooks/useAvailability
// from the pricing/availability response.
export interface AvailabilityEntry {
  startTime: string;
  endTime: string;
  availabilityFlag: string;
  spaceId: number;
  date: string;
  priceWithVat: number;
}

// The availability object is keyed by `${startTime}-${endTime}`.
export type Availability = Record<string, AvailabilityEntry[]>;

// Minimal shape of a raw booking item as consumed by the field builders.
export interface RawBookingItem {
  startTime: string;
  space?: { id?: number };
}

const getCombinedAvailability = (states: string[]) => {
  if (states.includes(AVAILABILITY_STATUSES.SERVICE_CLASH.flag)) {
    return AVAILABILITY_STATUSES.SERVICE_CLASH;
  }

  if (states.includes(AVAILABILITY_STATUSES.TIME_CLASH.flag)) {
    return AVAILABILITY_STATUSES.TIME_CLASH;
  }

  if (states.includes(AVAILABILITY_STATUSES.UNAVAILABLE.flag)) {
    return AVAILABILITY_STATUSES.TIME_CLASH;
  }

  if (states.includes(AVAILABILITY_STATUSES.AVAILABLE.flag)) {
    return AVAILABILITY_STATUSES.AVAILABLE;
  }

  return AVAILABILITY_STATUSES.UNKNOWN;
};

export const spaceDisabledForService =
  (service: { clinical?: boolean } | undefined) =>
  ({ clinical = false }: { clinical?: boolean }) => {
    if (!service) return false;
    return !service.clinical && clinical;
  };

export const combineSlots = (slots: Slot[] = []): Slot[] => {
  const m = (time: string) => moment(time, 'HH:mm');

  const reverseSortByTime = (
    { startTime: startA, endTime: endA }: Slot,
    { startTime: startB, endTime: endB }: Slot
  ) => {
    const startDiff = m(startB).diff(m(startA));
    return startDiff === 0 ? m(endB).diff(m(endA)) : startDiff;
  };

  const canCombine = ({ endTime }: Slot, { startTime }: Slot) =>
    m(endTime).add(60, 'minutes').isSameOrAfter(m(startTime));

  const hasGap = ({ endTime }: Slot, { startTime }: Slot) =>
    m(endTime).isBefore(m(startTime));

  return [...slots].sort(reverseSortByTime).reduce<Slot[]>(
    ([next, ...rest], curr) => [
      ...(next
        ? [
            ...(canCombine(curr, next)
              ? [
                  {
                    startTime: curr.startTime,
                    endTime: m(next.endTime).isBefore(m(curr.endTime))
                      ? curr.endTime
                      : next.endTime,
                    combined:
                      curr.combined || next.combined || hasGap(curr, next),
                  },
                ]
              : [{ combined: false, ...curr }, next]),
          ]
        : [{ combined: false, ...curr }]),
      ...rest,
    ],
    []
  );
};

export const separateSlot = (
  { startTime, endTime, ...rest }: Slot,
  availableSlots: Slot[] = []
): Slot[] => {
  const m = (time: string) => moment(time, 'HH:mm');

  const within = (slot: Slot, other: Slot) =>
    m(slot.startTime).isSameOrBefore(m(other.startTime)) &&
    m(slot.endTime).isSameOrAfter(m(other.endTime));

  const slotsWithin = availableSlots.filter(slot =>
    within({ startTime, endTime }, slot)
  );

  // Filter out slots contained within other (ie. all-day) slots
  return slotsWithin
    .filter(
      slot => slotsWithin.filter(other => within(other, slot)).length === 1
    )
    .map(slot => ({ ...slot, ...rest }));
};

export const matchSlots = <S extends Slot = Slot>(
  slots: S[],
  initialSlots: S[],
  distanceFn: ((a: S, b: S) => number) | undefined = undefined,
  mergeFn: ((a: S, b: S) => S) | undefined = undefined
): S[] => {
  const m = (time: string) => moment(time, 'HH:mm');

  const getDistance: (a: S, b: S) => number =
    distanceFn !== undefined
      ? distanceFn
      : (a, b) =>
          Math.abs(m(a.startTime).diff(m(b.startTime), 'minutes')) +
          Math.abs(m(a.endTime).diff(m(b.endTime), 'minutes'));

  const merge: (a: S, b: S) => S =
    mergeFn !== undefined
      ? mergeFn
      : ({ startTime, endTime }, slot) => ({
          ...slot,
          startTime,
          endTime,
        });

  const sortByMinDistance = (candidates: S[]) => (a: S, b: S) =>
    Math.min(...candidates.map(slot => getDistance(a, slot))) -
    Math.min(...candidates.map(slot => getDistance(b, slot)));

  const sortByStartTime = (a: S, b: S) => m(a.startTime).diff(m(b.startTime));

  const { matched } = [...slots]
    .sort(sortByMinDistance(initialSlots))
    .reduce<{ matched: S[]; unmatched: S[] }>(
      ({ matched, unmatched }, slot) => {
        const [match, ...rest] = [...unmatched].sort(sortByMinDistance([slot]));

        return {
          matched: [...matched, match ? merge(slot, match) : slot],
          unmatched: rest,
        };
      },
      { matched: [], unmatched: initialSlots }
    );

  return [...matched].sort(sortByStartTime);
};

export const matchBookingItems = (
  items: BookingItem[],
  initialItems: BookingItem[]
): BookingItem[] => {
  const unique = (values: string[]) =>
    Object.keys(
      values.reduce(
        (prev: Record<string, null>, curr: string) => ({
          ...prev,
          [curr]: null,
        }),
        {}
      )
    );

  const dateSort = (a: string, b: string) =>
    new Date(a).getTime() - new Date(b).getTime();
  const dates = unique(items.map(({ date }) => date)).sort(dateSort);

  const m = (time: string) => moment(time, 'HH:mm');

  const getDistance = (a: BookingItem, b: BookingItem): number =>
    Math.abs(m(a.startTime).diff(m(b.startTime), 'minutes')) +
    Math.abs(m(a.endTime).diff(m(b.endTime), 'minutes')) +
    (a.spaceId === b.spaceId ? 0 : 60 * 24);

  const merge = (
    { date, startTime, endTime, spaceId }: BookingItem,
    initialItem: BookingItem
  ): BookingItem => ({ ...initialItem, date, startTime, endTime, spaceId });

  return dates.flatMap(date =>
    matchSlots<BookingItem>(
      dates.length > 1 ? items.filter(item => item.date === date) : items,
      dates.length > 1
        ? initialItems.filter(item => item.date === date)
        : initialItems,
      getDistance,
      merge
    )
  );
};

const getSlotAvailability = (availability: Availability) => (slot: Slot) => {
  const states = Object.values(availability)
    .flat()
    .filter(
      ({ startTime, endTime }) =>
        startTime === slot.startTime && endTime === slot.endTime
    )
    .map(({ availabilityFlag }) => availabilityFlag);

  return {
    ...slot,
    available: getCombinedAvailability(states).flag,
    editable: true,
  };
};

export const getTimeSlots = (availability = {}, slots: Slot[] = []) =>
  slots.map(getSlotAvailability(availability));

const getSpaceAvailability = (
  spaceId: number,
  slot: AvailabilityEntry[],
  slots: Slot[]
) => {
  const states = slot
    .filter(entry => entry.spaceId === spaceId)
    .map(entry => entry.availabilityFlag);

  const [{ startTime, endTime }] = slot;

  const selectedSlot = slots.find(
    s => s.startTime === startTime && s.endTime === endTime
  );

  return {
    startTime,
    endTime,
    available: getCombinedAvailability(states).flag,
    combined: selectedSlot?.combined,
  };
};

export const getSlotsForSpace = (
  spaceId: number,
  slots: Slot[],
  availability: Availability,
  loading = false
) => {
  if (loading) return [];

  const combinedSlots = combineSlots(slots);

  return Object.values(availability)
    .filter(slot => slot.length > 0)
    .map(slot => getSpaceAvailability(spaceId, slot, combinedSlots));
};

export const slotHasError = (slotsForSpace: { available: string }[]) =>
  !!slotsForSpace.length &&
  slotsForSpace.every(
    slot => slot.available !== AVAILABILITY_STATUSES.AVAILABLE.flag
  );

export const getConflicts = (availability: Availability) =>
  Object.values(availability)
    .flat()
    .filter(
      ({ availabilityFlag }) =>
        availabilityFlag !== AVAILABILITY_STATUSES.AVAILABLE.flag
    );

export const getBookingTotal = (availability: Availability) =>
  Object.values(availability)
    .flat()
    .map(({ priceWithVat = 0 }) => priceWithVat)
    .reduce((prev, curr) => prev + curr, 0);

export const getAvailabilityStatus = (
  availability: Availability,
  startTime: string,
  endTime: string,
  spaceIds: number[] | undefined = undefined
) => {
  const key = `${startTime}-${endTime}`;

  const entries: AvailabilityEntry[] =
    key in availability ? availability[key] : [];

  const states = entries
    .filter(({ spaceId }) => !spaceIds || spaceIds.includes(spaceId))
    .map(({ availabilityFlag }) => availabilityFlag);

  return getCombinedAvailability(states);
};

// The widget holds the numeric recurrence fields as strings (they come off form
// inputs); recurrenceRuleBuilder coerces them, which is why this is looser than
// RecurrenceOptions itself.
export interface RecurrenceFormOptions {
  type?: RecurrenceOptions['type'];
  dayRange?: RecurrenceOptions['dayRange'];
  dayOfMonthRange?: string;
  everyNumberOf?: number | string;
  count?: number | string;
  weekDay?: string;
  weekDays?: string[];
  nthDayOfMonth?: number | string;
  weekNumber?: number | string;
  rangeEndType?: RecurrenceOptions['rangeEndType'];
  endDate?: Date | string;
}

export const getMinDate = (
  date: Date,
  recurrenceOptions: RecurrenceFormOptions = {}
) =>
  moment(
    nextOccurrence(date, {
      ...(recurrenceOptions as Partial<RecurrenceOptions>),
      rangeEndType: 'count',
      count: 2,
    })
  )
    .startOf('day')
    .toDate();

export const getMaxDate = (
  date: Date | string,
  { ccgCommissioned = false } = {}
) =>
  moment(date)
    .startOf('day')
    .add(
      ccgCommissioned ? CCG_BOOKING_RESTRICTION : NON_CCG_BOOKING_RESTRICTION,
      'months'
    )
    .toDate();

export const mapBookingItemsToSlots = (
  bookingItems: { startTime: string; endTime: string }[],
  status = AVAILABILITY_STATUSES.UNAVAILABLE.flag
) =>
  bookingItems.map(({ startTime, endTime }) => ({
    startTime: moment(startTime).format('HH:mm'),
    endTime: moment(endTime).format('HH:mm'),
    available: status,
  }));

const unique = <T>(value: T, index: number, list: T[]) =>
  list.indexOf(value) === index;

const getBookingItemDates = (items: RawBookingItem[] = []) =>
  items
    .map(({ startTime }) => moment(startTime).format(dateFormatForAPI))
    .filter(unique);

const getBookingItemSpaceIds = (date: string, items: RawBookingItem[] = []) =>
  items
    .filter(
      ({ startTime }) => moment(startTime).format(dateFormatForAPI) === date
    )
    .map(({ space: { id } = {} as { id?: number } }) => id)
    .filter(spaceId => spaceId !== undefined);

// Reads more of the item than the date/space helpers above: the widget's
// per-item fields carry the id, times and states through.
export interface BookingItemFieldsInput extends RawBookingItem {
  id?: number;
  endTime?: string;
  state?: string;
  statementState?: string;
}

export const getBookingItemFields = (items: BookingItemFieldsInput[] = []) =>
  getBookingItemDates(items)
    .map(date => ({
      [date]: getBookingItemSpaceIds(date, items)
        .map(spaceId => ({
          [`s${spaceId}`]: items
            .filter(
              ({ startTime }) =>
                moment(startTime).format(dateFormatForAPI) === date
            )
            .filter(
              ({ space: { id } = {} as { id?: number } }) => id === spaceId
            )
            .map(({ id, startTime, endTime, state, statementState }) => ({
              id,
              state,
              startTime: moment(startTime).format('HH:mm'),
              endTime: moment(endTime).format('HH:mm'),
              statementState,
            })),
        }))
        .reduce((prev, curr) => ({ ...prev, ...curr }), {}),
    }))
    .reduce((prev, curr) => ({ ...prev, ...curr }), {});

export const generateBookingItemFields = (
  initialDate: Date,
  spaceIds: number[],
  slots: Slot[],
  recurrence: Partial<RecurrenceOptions> | undefined = undefined
) =>
  (recurrence
    ? recurrenceRuleBuilder(initialDate, recurrence).all()
    : [initialDate]
  )
    .map(date => moment(date).format(dateFormatForAPI))
    .map(date => ({
      [date]: spaceIds
        .map(spaceId => ({
          [`s${spaceId}`]: slots,
        }))
        .reduce((prev, curr) => ({ ...prev, ...curr }), {}),
    }))
    .reduce((prev, curr) => ({ ...prev, ...curr }), {});

export const combineAvailability = (availabilityList: Availability[] = []) =>
  availabilityList.reduce(
    (prev: Availability, curr: Availability) =>
      Object.keys(curr)
        .map(key => ({
          [key]: key in prev ? [...prev[key], ...curr[key]] : curr[key],
        }))
        .reduce((prev_, curr_) => ({ ...prev_, ...curr_ }), prev),
    {}
  );

const filterNonEmpty = (item: Record<string, unknown>) =>
  Object.values(item).filter(value => value !== undefined).length > 0;

// The per-cell mapper's input and return types vary by caller (slots,
// availability objects, undefined-to-drop), so the transform is generic over
// the cell type going in and coming out.
const mapBookingItems = <In, Out>(
  fn: (item: In, spaceId: string, date: string, rowIndex: number) => Out,
  items: Record<string, Record<string, In>> = {}
) =>
  Object.keys(items)
    .sort((a, b) => new Date(a).getTime() - new Date(b).getTime())
    .map((date, rowIndex) => ({
      [date]: Object.keys(items[date])
        .map(spaceId => ({
          [spaceId]: fn(items[date][spaceId], spaceId, date, rowIndex),
        }))
        .filter(filterNonEmpty)
        .reduce((prev, curr) => ({ ...prev, ...curr }), {}),
    }))
    .filter(filterNonEmpty)
    .reduce((prev, curr) => ({ ...prev, ...curr }), {});

export const combineBookingItems = (items: CalendarItems) => {
  return mapBookingItems(combineSlots, items);
};

const filterAvailability = (
  availability: Availability,
  slots: Slot[],
  fn: (entry: AvailabilityEntry) => boolean
) =>
  combineSlots(slots)
    .map(({ startTime, endTime }) => `${startTime}-${endTime}`)
    .map(times =>
      times in availability ? { [times]: availability[times].filter(fn) } : {}
    )
    .reduce((prev, curr) => ({ ...prev, ...curr }), {});

export const getCalendarAvailability = (items = {}, availabilityList = []) => {
  const availability = combineAvailability(availabilityList);

  return mapBookingItems(
    (item: Slot[], spaceId: string, date: string) =>
      filterAvailability(
        availability,
        item,
        entry => `s${entry.spaceId}` === spaceId && entry.date === date
      ),
    items
  );
};

export const filterBookingItemsBySpaceIds = (spaceIds = [], items = {}) =>
  mapBookingItems(
    (item: Slot[], spaceId: string) =>
      spaceIds.map(id => `s${id}`).includes(spaceId) ? undefined : item,
    items
  );

export const getFirstSlotsForSpaceId = (
  spaceId: number,
  items: CalendarItems
): Slot[] => {
  const [date] = Object.keys(items)
    .filter(
      date =>
        items[date][`s${spaceId}`] && items[date][`s${spaceId}`].length > 0
    )
    .sort((a, b) => moment(a).diff(moment(b)));

  return date ? items[date][`s${spaceId}`] : [];
};

export const getSlotsForSpaceId = (
  spaceId: number,
  bookingItems: Record<string, Record<string, Slot[]>> = {},
  availabilityList: Availability[] = [],
  date = undefined
) =>
  availabilityList
    .flatMap(Object.values)
    .flat()
    .filter(slot => slot.spaceId === spaceId)
    .filter(slot => date === undefined || moment(slot.date).isSame(date, 'day'))
    .filter(
      slot =>
        bookingItems[slot.date] &&
        bookingItems[slot.date][`s${slot.spaceId}`] &&
        combineSlots(bookingItems[slot.date][`s${slot.spaceId}`]).some(
          ({ startTime, endTime }) =>
            startTime === slot.startTime && endTime === slot.endTime
        )
    );

export const getPriceForSpaceId = (
  ...args: Parameters<typeof getSlotsForSpaceId>
) =>
  getSlotsForSpaceId(...args).reduce(
    (prev, { priceWithVat }) => prev + priceWithVat,
    0
  );

export const isClinical = (spaces: { clinical?: boolean }[]) =>
  spaces.some(({ clinical }) => clinical);

export const isCancellableBookingItem = (bookingItem: {
  cancellable: boolean;
}) => {
  return bookingItem.cancellable;
};

export const isNotCancellableBookingItem = (bookingItem: {
  cancellable: boolean;
}) => {
  return !isCancellableBookingItem(bookingItem);
};

export const isClosed = (
  date: string | undefined,
  openingTimes: { [key: string]: string } | undefined = undefined,
  closures: Closure[] = []
): boolean => {
  const day = weekdays[moment(date).day()];
  return (
    (openingTimes &&
      (!openingTimes[`${day}Start`] || !openingTimes[`${day}End`])) ||
    closures.some(
      ({ startDate, endDate }) =>
        moment(date).isSame(moment(startDate)) ||
        moment(date).isBetween(
          moment(startDate).startOf('day'),
          moment(endDate).endOf('day')
        )
    )
  );
};

const groupBySpace = (availabilityList: Availability[]) => {
  const slots = availabilityList.flatMap(Object.values).flat();
  const spaceIds = slots.map(({ spaceId }) => spaceId).filter(unique);

  return spaceIds.map(id => slots.filter(slot => slot.spaceId === id));
};

// A booking item as read by the price grouping below.
interface PricedBookingItem {
  space: { id?: number; name?: string };
  totalCost?: number | string;
}

// A row of the pricing response; per-item costs come back with-VAT.
interface PricingItem {
  id?: number;
  spaceId: number | string;
  totalCostWithVat?: number | string;
  baseCostWithVat?: number | string;
}

export const groupSpacesAndPrices = ({
  isProvisional = false,
  existingBooking = undefined as
    | { bookingItems: PricedBookingItem[] }
    | undefined,
  bookingItems = {} as Record<string, Record<string, Slot[]>>,
  availabilityList = [],
  spaces = [] as Array<{ id: number; name: string }>,
  // pricing is used both as an array (length/filter) and as an object with an
  // `items` field depending on which endpoint produced it, so the type allows
  // both shapes at once.
  pricing = [] as PricingItem[] & { items?: PricingItem[] },
}) => {
  if (isProvisional) {
    if (existingBooking && pricing.length === 0) {
      return uniqBy(
        existingBooking.bookingItems.map(({ space: { id, name } }) => ({
          id,
          name,
          price: existingBooking.bookingItems
            .filter(item => item.space.id === id)
            .reduce((prev, { totalCost }) => prev + Number(totalCost), 0),
          basePrice: existingBooking.bookingItems
            .filter(item => item.space.id === id)
            .reduce((prev, { totalCost }) => prev + Number(totalCost), 0),
        })),
        'id'
      );
    }

    if (existingBooking && pricing && !pricing.items) {
      return uniqBy(
        existingBooking.bookingItems.map(({ space: { id, name } }) => ({
          id,
          name,
          price: pricing
            .filter(item => item.spaceId === id)
            .reduce(
              (prev, { totalCostWithVat }) => prev + Number(totalCostWithVat),
              0
            ),
          basePrice: pricing
            .filter(item => item.spaceId === id)
            .reduce(
              (prev, { baseCostWithVat }) => prev + Number(baseCostWithVat),
              0
            ),
        })),
        'id'
      );
    }

    return [];
  }

  if (pricing.length > 0) {
    return uniqBy(
      (pricing.items ?? []).map(price => ({
        id: price.id,
        // Pricing rows always reference one of the venue's spaces.
        name: spaces.find(({ id }) => id === Number(price.spaceId))!.name,
        price: price.totalCostWithVat,
        basePrice: price.baseCostWithVat,
      })),
      'id'
    );
  }

  return uniqBy(
    groupBySpace(availabilityList)
      .flat()
      .filter(({ spaceId }) => spaces.some(({ id }) => id === Number(spaceId)))
      .map(i => ({
        id: i.spaceId,
        name: spaces.find(({ id }) => id === Number(i.spaceId))!.name,
        price: groupBySpace(availabilityList)
          .flat()
          .filter(({ spaceId }) => spaceId === Number(i.spaceId))
          .filter(slot => bookingItems[slot.date])
          .filter(slot => {
            const combinedSlots = combineSlots(
              bookingItems[slot.date][`s${slot.spaceId}`]
            );

            return combinedSlots.some(
              ({ startTime, endTime }) =>
                startTime === slot.startTime && endTime === slot.endTime
            );
          })
          .reduce((prev, { priceWithVat }) => prev + Number(priceWithVat), 0),
      })),
    'id'
  );
};

export const getBookingState = ({
  bookingItems = [],
}: {
  bookingItems?: Array<{ state?: BookingState }>;
} = {}): BookingState => {
  const states = bookingItems
    .map(({ state }) => state)
    .filter((state): state is BookingState => !!state)
    .map(state =>
      state === BOOKING_STATES.expired ? BOOKING_STATES.tentative : state
    )
    .sort()
    .filter(unique);

  return states.length === 1 ? states[0] : BOOKING_STATES.tentative;
};
